import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { FullScreenCodeEditor } from '../questions';
import { getLanguageFromSkills, languageDisplayNames } from '../utils/languageDetection';

function CodeEditor({ 
  question, 
  value = '', 
  onChange, 
  disabled = false,
  label = 'Your code solution:' 
}) {
  // State for the selected language
  const [language, setLanguage] = useState(getLanguageFromSkills(question.skillsTested));
  
  // Update language when question ID changes (not on every re-render)
  useEffect(() => {
    setLanguage(getLanguageFromSkills(question.skillsTested));
  }, [question.id]);
  
  // Handle language change
  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  // Handle editor value change
  const handleEditorChange = (newValue) => {
    onChange(newValue);
  };

  // Handle editor mount - set up paste detection
  const handleEditorDidMount = (editor, monaco) => {
    // Add paste detection using Monaco's onDidPaste event
    editor.onDidPaste((e) => {
      // Get the pasted text from the range that was pasted
      const model = editor.getModel();
      if (model && e.range) {
        const pastedText = model.getValueInRange(e.range);
        
        // Log paste event - we need to access the cheating detection from parent
        // For now, we'll use a global approach
        if (window.logCheatingEvent) {
          window.logCheatingEvent({
            type: 'paste-attempt',
            content: pastedText
          });
        }
      }
    });
  };

  // State to track if the editor is in full screen mode
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  // Toggle full screen mode
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  // Fixed editor height
  const editorHeight = '240px';
  
  // If in full screen mode, render the FullScreenCodeEditor component
  if (isFullScreen) {
    return (
      <FullScreenCodeEditor
        question={question}
        value={value}
        onChange={onChange}
        disabled={disabled}
        onClose={toggleFullScreen}
        initialLanguage={language}
      />
    );
  }
  
  return (
    <div className="mt-2">
      <div className="mb-2 flex justify-between items-center">
        <div className="flex items-center">
          <label className="text-sm text-gray-600 mr-2">{label}</label>
          <select
            value={language}
            onChange={handleLanguageChange}
            className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded border-none focus:ring-2 focus:ring-blue-300"
            disabled={disabled}
          >
            {Object.entries(languageDisplayNames).map(([langKey, langName]) => (
              <option key={langKey} value={langKey}>
                {langName}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={toggleFullScreen}
          className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full flex items-center transition-colors duration-200"
          disabled={disabled}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
          </svg>
          Solve in Full Screen
        </button>
      </div>
      <div className="border border-gray-300 rounded overflow-hidden" style={{ height: editorHeight }}>
        <Editor
          height={editorHeight}
          defaultLanguage={language}
          language={language}
          value={value}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 14,
            fontFamily: '"Fira code", "Fira Mono", monospace',
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            lineNumbers: 'on',
            folding: true,
            renderLineHighlight: 'all',
            readOnly: disabled,
            fixedOverflowWidgets: true, // Ensures widgets like autocomplete don't get cut off
            suggest: {
              preview: true, // Show preview of the selected suggestion
              showStatusBar: true, // Show additional info at the bottom of the suggestion widget
              maxVisibleSuggestions: 12, // Limit number of visible suggestions
              filterGraceful: true, // Improves filtering
              showIcons: true, // Show icons in suggestions
              showMethods: true, // Show method suggestions
              showFunctions: true, // Show function suggestions
              showConstructors: true, // Show constructor suggestions
              showFields: true, // Show field suggestions
              showVariables: true, // Show variable suggestions
              showClasses: true, // Show class suggestions
              showStructs: true, // Show struct suggestions
              showInterfaces: true, // Show interface suggestions
              showModules: true, // Show module suggestions
              showProperties: true, // Show property suggestions
              showEvents: true, // Show event suggestions
              showOperators: true, // Show operator suggestions
              showUnits: true, // Show unit suggestions
              showValues: true, // Show value suggestions
              showConstants: true, // Show constant suggestions
              showEnums: true, // Show enum suggestions
              showEnumMembers: true, // Show enum member suggestions
              showKeywords: true, // Show keyword suggestions
              showWords: true, // Show word suggestions
              showColors: true, // Show color suggestions
              showFiles: true, // Show file suggestions
              showReferences: true, // Show reference suggestions
              showFolders: true, // Show folder suggestions
              showTypeParameters: true, // Show type parameter suggestions
              showSnippets: true, // Show snippet suggestions
            },
          }}
        />
      </div>
    </div>
  );
}

export default CodeEditor;
